import createLogger from '@sfly/aurora-js-logger';
import {
  BASE_URLS_PROD,
  IAM_FALLBACK_CONFIGS_PROD,
  SYNC_URL_3_0,
  AUTH_URL_3_0,
  DEFAULT_CONFIG,
  SFLY3_VISID_COOKIE,
  SFLY_FPID_COOKIE,
  PERSIST_USER_LOGIN_COOKIE_NAME,
  RECAPTCHA_SITE_KEY,
} from './Constants.js';
import uuid from 'uuid';
import { redactSensitiveStrings } from './LoggerUtil.js';
import { ClientErrors } from './ClientErrors.js';

// NOTE: we have "production" and "prod" configs since some clients pass one or the other. =(

/**
 * Client API Codes
 */

export const API_KEY_API3 = {
  dev: 'd9ylKEVogc9QtRmDnJyKFgk4DdK8iDG7',
  beta: 'd9ylKEVogc9QtRmDnJyKFgk4DdK8iDG7',
  kappa: 'd9ylKEVogc9QtRmDnJyKFgk4DdK8iDG7',
  stage: '6aGAIEelXSZ6AIt1KY7CJIiBCEq7LWy0',
  production: 'zlrHU7h6cTGTApk3IbOU0aVuxlEJkPeA',
  prod: 'zlrHU7h6cTGTApk3IbOU0aVuxlEJkPeA',
};
/**
 * Client API Codes
 */
export const API_KEY_API = {
  dev: 'XbnTgkZgRwKyNX9a90EdGGTBhvc6IXpk',
  beta: 'XbnTgkZgRwKyNX9a90EdGGTBhvc6IXpk',
  kappa: 'ms1BRq803Y0JGD7vJT3XDaASIdYD7S5L',
  stage: 'XbnTgkZgRwKyNX9a90EdGGTBhvc6IXpk',
  production: 'xbqGCOubdkYYypwETOHerYcReO5MUYdm',
  prod: 'xbqGCOubdkYYypwETOHerYcReO5MUYdm',
};

export const ENV = window.sflySsoConfig?.env
  ? window.sflySsoConfig.env.toLowerCase()
  : process.env.ENV;

/**
 * Cookie names are environment-specific: prod/production use the base name,
 * stage appends `_stage`, everything else (dev, beta, kappa) appends `_dev`.
 * Uses the effective runtime env, which sflySsoConfig.env can override.
 */
export function getEnvCookieName(baseName, env = ENV) {
  if (env === 'prod' || env === 'production') return baseName;
  return `${baseName}_${env === 'stage' ? 'stage' : 'dev'}`;
}

export const SFLY3_TRK_COOKIE_NAME = 'sfly3_trk';
export const SFLY3_TRK_COOKIE_VALUE = '3.0';

const URL_CONFIGS = {};

export function initCentralConfig() {
  const { enableAccounts2Config } = getCentralConfig();
  URL_CONFIGS.enableAccounts2Config = enableAccounts2Config;
  URL_CONFIGS.BASE_URLS = BASE_URLS_PROD;

  URL_CONFIGS.IAM_FALLBACK_CONFIGS = IAM_FALLBACK_CONFIGS_PROD;

  URL_CONFIGS.BASE_URLS_TOKEN = URL_CONFIGS.BASE_URLS[ENV].token;
  URL_CONFIGS.BASE_URL_SSO = URL_CONFIGS.BASE_URLS[ENV].sso;
  URL_CONFIGS.BASE_URL_API2 = URL_CONFIGS.BASE_URLS[ENV].api2;
  URL_CONFIGS.BASE_URL_WWW = URL_CONFIGS.BASE_URLS[ENV].www;
  URL_CONFIGS.BASE_URL_TP = URL_CONFIGS.BASE_URLS[ENV].tp;
  URL_CONFIGS.BASE_URL_SHARE = URL_CONFIGS.BASE_URLS[ENV].share;
  URL_CONFIGS.BASE_URL_GUEST_API = URL_CONFIGS.BASE_URLS_TOKEN.sfly;
  URL_CONFIGS.IAM_CONFIG_FALLBACK = URL_CONFIGS.IAM_FALLBACK_CONFIGS[ENV];
  URL_CONFIGS.BASE_URL_SFLY_ACCOUNT_API =
    URL_CONFIGS.BASE_URLS[ENV].sflyaccount;
}

export const getURLConfigs = () => URL_CONFIGS;

/**
 * Recaptcha enterprise site key for the current env, used to generate a
 * recaptcha token for flows that need one (e.g. brand OTP sign in).
 * @returns {string}
 */
export function getRecaptchaSiteKey() {
  return RECAPTCHA_SITE_KEY[ENV] || '';
}

export function is_2_0_ConfigEnable() {
  return getURLConfigs().enableAccounts2Config;
}

export function getAuthUrl() {
  return AUTH_URL_3_0;
}
export function getSyncUrl() {
  return `${getTokenBaseUrlByHostName()}${SYNC_URL_3_0}`;
}

const PROVIDERS = {
  FACEBOOK: 'fb',
  GOOGLE: 'gs',
  APPLE: 'as',
  AMAZON: 'az',
};

/**
 * Main client logger for splunk
 */
const globalLogData = {};
// Ignore eslint error for Webpack.DefinePlugin environment variables
if (process.env.CI_BUILD_UUID) {
  globalLogData.appVersion = process.env.CI_BUILD_UUID;
} else {
  globalLogData.appVersion = 'local-dev-version';
}

export const logger = createLogger({
  name: 'sfly-sso-client',
  globalLogData,
  enableNRD: true,
  batchInterval: 1000,
  batchSize: 10,
  env: process.env.ENV === 'prod' ? 'production' : process.env.ENV,
  apiKey: API_KEY_API3[process.env.ENV],
});

/**
 * Helper method to add query params to the URLs
 *
 * @returns {*}
 */
export function urlHelper(url, configObject) {
  // redirectUri, brand, source, additionalQueryStringParams
  const urlParams = [];

  // Clone the config so we can alter it and not mess up the original.
  const config = Object.assign({}, configObject);
  if (config) {
    // If no redirectUri is passed we will use the current window location
    if (config.redirectUri || config.redirectUri === '') {
      // Passing an empty string stops the auto redirect back to the page and allows the default SPA actions
      // to kick in. This is the default behavior for the signout() call.
      if (config.redirectUri.length > 0) {
        urlParams.push(`redirectUri=${encodeURIComponent(config.redirectUri)}`);
      }
      // Delete so we don't add it twice
      delete config.redirectUri;
    } else {
      urlParams.push(`redirectUri=${encodeURIComponent(window.location.href)}`);
    }

    // Lifetouch Migration changeAccountUri
    if (config.changeAccountUri || config.changeAccountUri === '') {
      if (config.changeAccountUri.length > 0) {
        urlParams.push(
          `changeAccountUri=${encodeURIComponent(config.changeAccountUri)}`,
        );
      }
      // Delete so we don't add it twice
      delete config.changeAccountUri;
    }

    for (let i in config) {
      if (
        typeof config[i] === 'string' ||
        typeof config[i] === 'boolean' ||
        typeof config[i] === 'number'
      ) {
        urlParams.push(`${i}=${config[i]}`);
      }
    }
  } else {
    urlParams.push(`redirectUri=${encodeURIComponent(window.location.href)}`);
  }

  return `${url}?${urlParams.join('&')}`;
}

/**
 * Get a cookie (use to check feature flag cookie)
 * @param cookieName
 * @returns {*}
 */
export function getCookieValue(cookieName) {
  if (typeof document === 'undefined' || typeof document.cookie !== 'string') {
    return null;
  }
  const match = document.cookie.match(
    new RegExp('(^| )' + cookieName + '=([^;]+)'),
  );
  if (match) {
    return match[2];
  }
  return null;
}

export function setCookie(cookieName, cookieVal, exp, domain) {
  const expires = 'expires=' + exp.toUTCString();
  if (domain) {
    document.cookie =
      cookieName +
      '=' +
      cookieVal +
      ';' +
      expires +
      ';domain=' +
      domain +
      ';path=/';
  } else {
    document.cookie = cookieName + '=' + cookieVal + ';' + expires + ';path=/';
  }
}

// For 2.0, we need this
export function isValidUrlForSessionRestart(href) {
  let isValid = true;
  if (href.indexOf('shutterfly.com') > -1 && href.indexOf('signout/') > -1) {
    isValid = false;
  }
  return isValid;
}

/**
 * Helper methodd that returns the proper token URL host to get us around cross domain cookie syncing.
 * We must extend tokens and sync tokens from the proper host to avoid security issues in Safari and IE 11.
 * @returns {string|*}
 */
export function getTokenBaseUrlByHostName() {
  const { hostname } = window.location;
  // Default token base url for shutterfly.com domain (Chrome & Not so strict browsers)
  let baseUrl = getURLConfigs().BASE_URLS_TOKEN.sfly;
  if (hostname.indexOf('tinyprints.com') !== -1) {
    baseUrl = getURLConfigs().BASE_URLS_TOKEN.tp;
  } else if (
    hostname.indexOf('my.lifetouch.com') !== -1 ||
    hostname.indexOf('my.lifetouch.net') !== -1
  ) {
    baseUrl = getURLConfigs().BASE_URLS_TOKEN.mlt;
  } else if (
    hostname.indexOf('community.lifetouch.com') !== -1 ||
    hostname.indexOf('community.lifetouch.net') !== -1
  ) {
    baseUrl = getURLConfigs().BASE_URLS_TOKEN.community;
  } else if (
    hostname.indexOf('prestigeportraits.com') !== -1 ||
    hostname.indexOf('solo-r.lifetouch.net') !== -1
  ) {
    baseUrl = getURLConfigs().BASE_URLS_TOKEN.prestige;
  } else if (
    hostname.indexOf('lifetouchsmiles.com') !== -1 ||
    hostname.indexOf(
      `https://ltsmiles-${ENV === 'stage' ? 'qa' : ENV}.d.applicationx.net`,
    ) !== -1
  ) {
    // For preschools and ltsmiles host name is same at the end. Added full host name for ltsmiles(Above)
    baseUrl = getURLConfigs().BASE_URLS_TOKEN.ltsmiles;
  } else if (
    hostname.indexOf('mycollection.lifetouch.com') !== -1 ||
    hostname.indexOf('mycollection.lifetouch.net') !== -1
  ) {
    baseUrl = getURLConfigs().BASE_URLS_TOKEN.mycollection;
  } else if (
    hostname.indexOf('preschoolsmiles3.lifetouch.net') !== -1 ||
    hostname.indexOf('preschool3.lifetouch.com') !== -1
  ) {
    baseUrl = getURLConfigs().BASE_URLS_TOKEN.preschools;
  } else if (
    is_2_0_ConfigEnable() &&
    (hostname.indexOf('preschoolsmiles.com') !== -1 ||
      hostname.indexOf('d.applicationx.net') !== -1 ||
      hostname.indexOf('lifetouch.net') !== -1 ||
      hostname.indexOf('lifetouch.com') !== -1)
  ) {
    baseUrl = getURLConfigs().BASE_URLS_TOKEN.preschools;
  }
  return baseUrl;
}

/**
 * Normalizes the Cognito Groups into values that are more human readable.
 * @param groups
 * @param socialOnlyAccount
 * @returns {Array}
 */
export function normalizeCognitoGroups(groups, socialOnlyAccount = false) {
  const normalized = [];
  if (groups && groups.length > 0) {
    for (let i = 0; i < groups.length; i++) {
      let groupName = groups[i].toLowerCase();
      if (groupName.indexOf(PROVIDERS.FACEBOOK) !== -1) {
        normalized.push('Facebook');
      } else if (groupName.indexOf(PROVIDERS.GOOGLE) !== -1) {
        normalized.push('Google');
      } else if (groupName.indexOf(PROVIDERS.APPLE) !== -1) {
        normalized.push('Apple');
      } else if (groupName.indexOf(PROVIDERS.AMAZON) !== -1) {
        normalized.push('Amazon');
      } else if (socialOnlyAccount === false) {
        /**
         *  This uitility is being used at multiple place .
         *  We don't need to include cognito:user group in socialProviders
         */
        normalized.push(groups[i]);
      }
    }
  }
  return normalized;
}

/**
 * Get a made up sudo-random transaction id for logging and error linkage
 * @returns {string}
 */
export function getTransactionId() {
  //  if pageTransactionInfo present in localStorage use it, if not set in local strage, and this might be overridden by ssofe pages at any point in time
  const pageTransactionInfo = window.localStorage.getItem(
    'pageTransactionInfo',
  );
  if (!pageTransactionInfo) {
    const txId = uuid();
    window.localStorage.setItem(
      'pageTransactionInfo',
      JSON.stringify({
        page: 'zuul',
        txId,
      }),
    );
    return txId;
  } else if (pageTransactionInfo) {
    return JSON.parse(pageTransactionInfo).txId;
  }
}

export function getStandardErrorObject(clientError, transId) {
  return { error: clientError, transId: transId };
}

export function isStandardErrorObject(obj) {
  if (typeof obj !== 'object') return false;
  if ('error' in obj && 'transId' in obj) return true;
  return false;
}

export function logError(clientError, realError, transId) {
  logger.error(
    redactSensitiveStrings({
      appContext: {
        msg: `${clientError.code} - ${clientError.splunkLabel}`,
        error: JSON.stringify(clientError),
        // realError already carries the full detail (name/message/stack/etc,
        // via describeFetchFailure() or an explicit field list at each call
        // site) as structured JSON - a second, flattened `errorStringify`
        // string field only re-shipped the same stack bytes a second time
        // and, worse, silently dropped everything except the stack whenever
        // realError had one. Query serviceLog.appContext.realError.* instead.
        realError: realError,
        url: window.location,
        'Sfly-Transactionid': transId ?? '',
        sfly3_visid: getCookieValue(getEnvCookieName(SFLY3_VISID_COOKIE)),
        sfly_fpid: getCookieValue(getEnvCookieName(SFLY_FPID_COOKIE)),
        persistUserLogin: getCookieValue(PERSIST_USER_LOGIN_COOKIE_NAME),
      },
    }),
  );
  logger.dispatchLogs();
}

// Same shape as logError, for failures we expect and deliberately ignore.
export function logWarn(clientError, realError, transId) {
  logger.warn(
    redactSensitiveStrings({
      appContext: {
        msg: `${clientError.code} - ${clientError.splunkLabel}`,
        error: JSON.stringify(clientError),
        realError: realError,
        url: window.location,
        'Sfly-Transactionid': transId ?? '',
        sfly3_visid: getCookieValue(getEnvCookieName(SFLY3_VISID_COOKIE)),
        sfly_fpid: getCookieValue(getEnvCookieName(SFLY_FPID_COOKIE)),
        persistUserLogin: getCookieValue(PERSIST_USER_LOGIN_COOKIE_NAME),
      },
    }),
  );
  logger.dispatchLogs();
}

export function logInfo(msg) {
  logger.info(
    redactSensitiveStrings({
      appContext: {
        msg,
        'Sfly-Transactionid': getTransactionId(),
        sfly3_visid: getCookieValue(getEnvCookieName(SFLY3_VISID_COOKIE)),
        sfly_fpid: getCookieValue(getEnvCookieName(SFLY_FPID_COOKIE)),
        persistUserLogin: getCookieValue(PERSIST_USER_LOGIN_COOKIE_NAME),
      },
    }),
  );
  logger.dispatchLogs();
}

export const WINDOW_FEATURES_VALID_PROPERTIES = [
  'popup',
  'width',
  'innerWidth',
  'height',
  'innerHeight',
  'left',
  'screenX',
  'top',
  'screenY',
  'noopener',
  'noreferrer',
];

export const RESYNC_TOKENS_SUCCESS_SHOW_SIGN_IN_NO_REDIRECT =
  'RESYNC_TOKENS_SUCCESS_SHOW_SIGN_IN_NO_REDIRECT';

//Initiate interceptor for authenticate call
/**
 * Fire-and-forget call to the SSO sync endpoint so the server reissues the
 * IdToken/AccessToken cookies after a refresh-token call. Not awaited and its
 * result is ignored; failures are only logged as warnings. Amplify already
 * stored the new tokens in MyStorage, so MyStorage.sync() is not needed.
 * @param {typeof fetch} fetchFn The unpatched fetch, so this call never
 *   re-enters the interceptor.
 */
export function syncSsoCookiesAfterRefresh(fetchFn) {
  const warnSyncFailure = (e) =>
    logWarn(ClientErrors.TOKEN_SYNC_AFTER_REFRESH, {
      name: e?.name,
      message: e?.message,
    });
  try {
    fetchFn(getSyncUrl(), {
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
        ...getLoggingHeaders(),
      },
    }).catch(warnSyncFailure);
  } catch (e) {
    warnSyncFailure(e);
  }
}

export const initiateInterceptor = () => {
  if (!window.fetchPatched) {
    window.fetchPatched = true; // only patch window.fetch once to prevent recursion issue

    const originalFetch = fetch;
    window.fetch = async (...args) => {
      const [resource, config] = args;
      // config.credentials = 'include';
      if (
        config &&
        typeof config === 'object' &&
        config['method'] &&
        (config.method === 'POST' || config.method === 'GET') &&
        typeof resource === 'string' &&
        resource.includes(getAuthUrl())
      ) {
        // Try to extract accountId from the URL if it exists
        let accountId;
        if (typeof resource === 'string') {
          const accountIdMatch = resource.match(/[?&]accountId=([^&]*)/);
          if (accountIdMatch && accountIdMatch[1]) {
            accountId = accountIdMatch[1];
          }
        }
        config.headers = {
          ...(config.headers || {}),
          'Content-Type': 'application/json',
          'SFLY-Apikey': API_KEY_API[ENV],
          'x-api-key': API_KEY_API3[ENV],
          'sfly-application-name': 'sflyssofe',
          ...getLoggingHeaders(accountId),
        };
      }

      // Ensure credentials are included to send cookies with the request
      // We have commented the below code due to ACCT-4255
      // if (config && typeof config === 'object') {
      //   config.credentials = 'include';
      // }

      const response = await originalFetch(resource, config);

      // After a successful refresh-token call, ask the SSO server to reissue the
      // IdToken/AccessToken cookies so other tabs pick up the new session.
      if (
        response?.ok &&
        config?.method === 'POST' &&
        typeof resource === 'string' &&
        resource.includes('/v3/oauth2') &&
        typeof config.body === 'string' &&
        config.body.includes('REFRESH_TOKEN')
      ) {
        syncSsoCookiesAfterRefresh(originalFetch);
      }

      // response interceptor here
      return response;
    };
  }
};

/**
mapping for web case form serive cloud (salesforce)
*/

export const FORM_INPUT_ID_MAPPING = {
  dev: {
    firstName: '00N1I00000ObNwN',
    lastName: '00N1I00000ObNwW',
    email: 'email',
    telephoneNumber: 'phone',
    streetAddress1: '00N1I00000ONe0U',
    streetAddress2: '00N1I00000ONe0V',
    city: '00N1I00000ONe0W',
    state: '00N1I00000ONe0c',
    countryCode: '00N11000006cFAF',
    postalCode: '00N1I00000MGagO',
    orgid: '00D7A000000A7VL',
    userId: '00N1I00000OO1Nf',
    recordType: '0121I000000adlb',
    requestType: '00N1I00000OO1Ng',
    isCCPA: '00N3s00000PJTve',
  },
  beta: {
    firstName: '00N1I00000ObNwN',
    lastName: '00N1I00000ObNwW',
    email: 'email',
    telephoneNumber: 'phone',
    streetAddress1: '00N1I00000ONe0U',
    streetAddress2: '00N1I00000ONe0V',
    city: '00N1I00000ONe0W',
    state: '00N1I00000ONe0c',
    countryCode: '00N11000006cFAF',
    postalCode: '00N1I00000MGagO',
    orgid: '00D7A000000A7VL',
    userId: '00N1I00000OO1Nf',
    recordType: '0121I000000adlb',
    requestType: '00N1I00000OO1Ng',
    isCCPA: '00N3s00000PJTve',
  },
  kappa: {
    firstName: '00N1I00000ObNwN',
    lastName: '00N1I00000ObNwW',
    email: 'email',
    telephoneNumber: 'phone',
    streetAddress1: '00N1I00000ONe0U',
    streetAddress2: '00N1I00000ONe0V',
    city: '00N1I00000ONe0W',
    state: '00N1I00000ONe0c',
    countryCode: '00N11000006cFAF',
    postalCode: '00N1I00000MGagO',
    orgid: '00D7A000000A7VL',
    userId: '00N1I00000OO1Nf',
    recordType: '0121I000000adlb',
    requestType: '00N1I00000OO1Ng',
    isCCPA: '00N3s00000PJTve',
  },
  stage: {
    firstName: '00N1I00000ObNwN',
    lastName: '00N1I00000ObNwW',
    email: 'email',
    telephoneNumber: 'phone',
    streetAddress1: '00N1I00000ONe0U',
    streetAddress2: '00N1I00000ONe0V',
    city: '00N1I00000ONe0W',
    state: '00N1I00000ONe0c',
    countryCode: '00N11000006cFAF',
    postalCode: '00N1I00000MGagO',
    orgid: '00D7A000000A7VL',
    userId: '00N1I00000OO1Nf',
    recordType: '0121I000000adlb',
    requestType: '00N1I00000OO1Ng',
    isCCPA: '00N3s00000PJTve',
  },
  production: {
    firstName: '00N1I00000ObNwN',
    lastName: '00N1I00000ObNwW',
    email: 'email',
    state: '00N1I00000ONe0c',
    telephoneNumber: 'phone',
    streetAddress1: '00N1I00000ONe0U',
    streetAddress2: '00N1I00000ONe0V',
    city: '00N1I00000ONe0W',
    countryCode: '00N1I00000OO1Ne',
    postalCode: '00N1I00000MGagO',
    orgid: '00D1I000002Jf04',
    userId: '00N1I00000OO1Nf',
    recordType: '0121I000000adlb',
    requestType: '00N1I00000OO1Ng',
    isCCPA: '00N3s00000PJTve',
  },
  prod: {
    firstName: '00N1I00000ObNwN',
    lastName: '00N1I00000ObNwW',
    email: 'email',
    state: '00N1I00000ONe0c',
    telephoneNumber: 'phone',
    streetAddress1: '00N1I00000ONe0U',
    streetAddress2: '00N1I00000ONe0V',
    city: '00N1I00000ONe0W',
    countryCode: '00N1I00000OO1Ne',
    postalCode: '00N1I00000MGagO',
    orgid: '00D1I000002Jf04',
    userId: '00N1I00000OO1Nf',
    recordType: '0121I000000adlb',
    requestType: '00N1I00000OO1Ng',
    isCCPA: '00N3s00000PJTve',
  },
};

/*flattenObject takes object to be flatten
 */

export const flattenObject = (obj) => {
  const flattened = {};

  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      Object.assign(flattened, flattenObject(obj[key]));
    } else {
      flattened[key] = obj[key];
    }
  });

  return flattened;
};

export const getSalesforcePostEndpoint = () => {
  if (ENV === 'production' || ENV === 'prod') {
    return 'https://webto.salesforce.com/servlet/servlet.WebToCase?encoding=UTF-8';
  }
  return 'https://shutterfly--sfly1.sandbox.my.salesforce.com/servlet/servlet.WebToCase?encoding=UTF-8';
  //return 'https://shutterflyuat--Sfly1.cs18.my.salesforce.com/servlet/servlet.WebToCase?encoding=UTF-8';
};

export function postRequestForm(formData) {
  // submitting form data
  const customHiddenIframeName = 'SFLY_Web2Case';
  if (!document.getElementById(customHiddenIframeName)) {
    var theiFrame = document.createElement('iframe');
    theiFrame.id = customHiddenIframeName;
    theiFrame.name = customHiddenIframeName;
    theiFrame.src = 'about:blank';
    theiFrame.style.display = 'none';
    document.body.appendChild(theiFrame);
  }
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = getSalesforcePostEndpoint();
  form.setAttribute('target', customHiddenIframeName);
  for (let fieldName in formData) {
    const theInput = document.createElement('input');
    theInput.name = fieldName;
    theInput.value = formData[fieldName];
    theInput.setAttribute('type', 'hidden');
    form.appendChild(theInput);
  }
  document.body.appendChild(form);
  form.submit();
}

/*
 * Main function to get central configuration
 */
export function getCentralConfig() {
  return DEFAULT_CONFIG[ENV];

  // try {
  //   return await fetchConfigFromAPI();
  // } catch (error) {
  //   console.log('Error fetching central config data', error);
  //   return DEFAULT_CONFIG[ENV];
  // }
}

// async function fetchConfigFromAPI() {
//   let response = await fetch(CCS_URL_3_0[ENV], {
//     headers: {
//       'sfly-ccs-api-key': CCS_KEY_3_0[ENV],
//       'X-Api-Key': API_KEY_API3[ENV],
//       'Content-Type': 'application/json',
//     },
//   });
//   if (response.status !== 200) {
//     throw new Error(`Failed to fetch config: ${response.statusText}`);
//   }

//   const data = await response.json();
//   if (!data || typeof data !== 'object') {
//     throw new Error('Invalid config data received');
//   }

//   return data;
// }

export function extractDomain(url) {
  try {
    const { hostname } = new URL(url);
    return hostname;
  } catch (error) {
    console.error('Invalid URL:', url);
    return null;
  }
}

export function getLoggingHeaders(accountId) {
  return {
    'sfly-transactionid': getTransactionId(),
    noodle: getTransactionId(),
    accountId: accountId || window.$sflyAuth?.currentUser?.sfly_uid,
    'x-api-key': API_KEY_API3[ENV],
  };
}

/**
 * Creates a signal that aborts after `timeoutMs`. Prefers the native
 * AbortSignal.timeout(); on engines that don't have it (older than this package's
 * stated browser floor), falls back to a manual AbortController + timer so those
 * engines still get a real, working timeout instead of every fetchWithTimeout()
 * call throwing synchronously (AbortSignal.timeout is not a function), which would
 * otherwise get misread as a network error, waste the one retry, and fail outright
 * on every single request. The fallback's plain `controller.abort()` (no reason)
 * produces a DOMException named 'AbortError', which classifyFetchError already
 * recognizes as a timeout once we confirm it's *this* signal that fired.
 */
function createTimeoutSignal(timeoutMs) {
  if (typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(timeoutMs);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(), timeoutMs);
  return controller.signal;
}

/**
 * Classifies a fetch() rejection against the timeout signal that was live for the
 * attempt that threw it. `ownTimeoutSignal` lets us tell "our AbortSignal.timeout()
 * fired" apart from "the caller's own signal fired" - the latter is a deliberate
 * external cancellation (e.g. navigation), not a timeout, so it must not be retried
 * or logged as one. Per the Fetch spec, fetch() only ever rejects (as opposed to
 * resolving with a non-ok response) for network-level failures, and always with a
 * TypeError.
 */
function classifyFetchError(err, ownTimeoutSignal) {
  const isTimeout =
    err.name === 'TimeoutError' ||
    (err.name === 'AbortError' && ownTimeoutSignal?.aborted);
  const isNetworkError = err instanceof TypeError;
  return {
    isTimeout,
    isNetworkError,
    isRetryable: isTimeout || isNetworkError,
  };
}

/**
 * Wraps fetch() with a client-side timeout via AbortSignal.timeout(), plus a single
 * automatic retry when that attempt times out or fails with a network error. Retrying
 * is only safe for idempotent requests (GETs, fire-and-forget "extend/sync" calls) -
 * callers whose request has a side effect that shouldn't happen twice (e.g. account
 * creation, sending an OTP code) must pass { retry: false } so a stalled-but-processed
 * request doesn't get resubmitted.
 *
 * If the caller passes its own `options.signal` (e.g. to cancel on navigation), it's
 * combined with our timeout signal via AbortSignal.any() where supported, so external
 * cancellation still works instead of being silently overridden. On engines without
 * AbortSignal.any() (older than this package's stated browser floor), the caller's
 * signal is not honored and only our timeout applies - there's no polyfill for it here,
 * consistent with the rest of this helper.
 *
 * fetch() itself only resolves once response *headers* arrive - it does not wait
 * for the body. So the exact failure mode this helper exists for ("proxy returns
 * headers but stalls on the body") would otherwise sail straight past fetch()'s
 * own await, return to the caller as a "successful" response, and only surface
 * later and unguarded in the caller's own result.json() - missing our timeout, our
 * retry, and our logging entirely. To cover it, each attempt also drains a *clone*
 * of the body (discarding it) before resolving, under the same timeout, and hands
 * back the pristine, unread original response for the caller to read normally -
 * so none of the 9 call sites need to change.
 *
 * Only a failure that survives the retry is logged here (as HTTP_TIMEOUT /
 * HTTP_NETWORK_ERROR) - a first attempt that fails but succeeds on retry is expected
 * to happen sometimes and isn't actionable, so it's silent. When the error isn't
 * retryable, or retry is disabled, this helper doesn't log at all - the caller's own
 * .catch() is expected to log a call-site-specific error, same as before this helper
 * existed.
 * @param {string} url
 * @param {object} [options] - standard fetch() options
 * @param {object} [config]
 * @param {number} [config.timeoutMs=3000]
 * @param {boolean} [config.retry=true]
 * @returns {Promise<Response>}
 */
export async function fetchWithTimeout(
  url,
  options = {},
  { timeoutMs = 3000, retry = true } = {},
) {
  let ownTimeoutSignal;
  const attempt = async () => {
    ownTimeoutSignal = createTimeoutSignal(timeoutMs);
    const signal =
      options.signal && typeof AbortSignal.any === 'function'
        ? AbortSignal.any([options.signal, ownTimeoutSignal])
        : ownTimeoutSignal;
    const response = await fetch(url, { ...options, signal });
    await response.clone().text();
    return response;
  };

  try {
    return await attempt();
  } catch (err) {
    const { isRetryable } = classifyFetchError(err, ownTimeoutSignal);
    if (!isRetryable || !retry) {
      // Not retried: the caller's own .catch() logs a call-site-specific error for
      // this case, same as before this helper existed.
      throw err;
    }
    // Small fixed delay before the single retry rather than an immediate re-hit,
    // since an instant retry against an already-stalling/unreachable service rarely helps.
    await new Promise((resolve) => setTimeout(resolve, 250));
    try {
      return await attempt();
    } catch (retryErr) {
      // Only log here: the retry also failed, so this is the actionable case.
      logHttpFailure(url, retryErr, ownTimeoutSignal, timeoutMs);
      throw retryErr;
    }
  }
}

// A fetch rejection captures the whole synchronous call chain, so the stack
// runs past our own code and on into whichever app called us - shared by every
// call site that logs a raw fetch/JSON error (this file's logHttpFailure and
// MyStorage.js's own catch handlers) so none of them ship the caller's entire
// app stack to Splunk uncapped.
//
// Match our bundle as a path segment rather than a bare substring: a consuming
// app is free to name something `sso-client-wrapper.js`, and a plain `includes`
// would treat that caller frame as ours and keep everything above it. A frame
// under a literal `/sso-client/` directory is our code, whether it is served
// from /micro/sso-client/ or bundled from node_modules/sso-client/.
const STACK_OWN_FRAME = /\/sso-client\/[^\s)]*\.js/;
// Used only when no own frame is found (renamed bundle, unexpected thrower).
const STACK_FALLBACK_FRAMES = 8;
const STACK_MAX_CHARS = 1200;
const STACK_ELLIPSIS = '…';

export function trimStack(stack) {
  if (typeof stack !== 'string') return stack;
  const lines = stack.split('\n');
  let lastOwnFrame = -1;
  // Skip index 0 — that is the message, not a frame.
  for (let i = lines.length - 1; i > 0; i--) {
    if (STACK_OWN_FRAME.test(lines[i])) {
      lastOwnFrame = i;
      break;
    }
  }
  const keep =
    lastOwnFrame > 0
      ? lastOwnFrame + 1
      : Math.min(lines.length, STACK_FALLBACK_FRAMES);
  const trimmed = lines.slice(0, keep).join('\n');
  // Count the ellipsis against the budget so the result never exceeds it.
  return trimmed.length > STACK_MAX_CHARS
    ? trimmed.slice(0, STACK_MAX_CHARS - STACK_ELLIPSIS.length) + STACK_ELLIPSIS
    : trimmed;
}

function logHttpFailure(url, err, ownTimeoutSignal, timeoutMs) {
  const { isTimeout } = classifyFetchError(err, ownTimeoutSignal);
  const transId = getTransactionId();
  logError(
    isTimeout ? ClientErrors.HTTP_TIMEOUT : ClientErrors.HTTP_NETWORK_ERROR,
    {
      url,
      timeoutMs,
      // Error/DOMException's name, message and stack are all non-enumerable (or,
      // for name, inherited from the prototype) - {...err} silently drops every one
      // of them. Read them explicitly so the log is actually actionable.
      name: err?.name,
      message: err?.message,
      stack: trimStack(err?.stack),
    },
    transId,
  );
}
